import { useMemo } from "react";
import { PaymentsSubscriptionStatusResponseDto } from "@advanced-sales-technologies/backend-client/models/PaymentsSubscriptionStatusResponseDto";
import { useTranslation } from "react-i18next";

import { Subscription } from "@/types/Subscription.type";

type Props = {
  subscription: Subscription;
  paymentType: "month" | "year";
  activeSubscription: Subscription;
  plannedDowngrade?: PaymentsSubscriptionStatusResponseDto;
};

const useSubscription = ({ subscription, activeSubscription, paymentType, plannedDowngrade }: Props) => {
  const { t } = useTranslation();

  const subscriptionPrice = useMemo(
    () => subscription.prices.find(({ interval }) => interval === paymentType) || subscription.prices[0],
    [subscription, paymentType],
  );

  const activeSubscriptionPrice = useMemo(
    () => activeSubscription.prices.find(({ interval }) => interval === paymentType) || activeSubscription.prices[0],
    [activeSubscription, paymentType],
  );

  const downgradeDate = useMemo(() => {
    if (!plannedDowngrade || !plannedDowngrade.is_downgrade || subscriptionPrice.id !== plannedDowngrade.target_price)
      return null;

    return new Date(plannedDowngrade.start_date!);
  }, [plannedDowngrade, subscriptionPrice]);

  const remainingDaysToDowngrade = useMemo(() => {
    if (!downgradeDate) return -1;

    const diff = downgradeDate.getTime() - new Date().getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  }, [downgradeDate]);

  const isUpgrade = useMemo(
    () => subscriptionPrice.unit_amount > activeSubscriptionPrice.unit_amount,
    [subscription, activeSubscription],
  );

  const calcYearlyDiscountPercent = useMemo(() => {
    if (paymentType === "month" || subscription.prices.length !== 2) return 0;

    const monthly = subscription.prices.find(({ interval }) => interval === "month");
    const yearly = subscription.prices.find(({ interval }) => interval === "year");

    return Math.round((1 - (monthly!.unit_amount * 12) / yearly!.unit_amount) * 100);
  }, [subscriptionPrice, activeSubscriptionPrice, paymentType]);

  const isActiveSubscription = useMemo(
    () => activeSubscription.id === subscription.id,
    [activeSubscription, subscription],
  );

  const isActiveInterval = useMemo(
    () => activeSubscriptionPrice.interval === paymentType,
    [activeSubscriptionPrice, paymentType],
  );

  const formattedSubscriptionPrice =
    subscriptionPrice.unit_amount >= 0
      ? new Intl.NumberFormat(navigator.languages, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(subscriptionPrice.unit_amount / 100)
      : t("settings.plans.talk");

  return {
    subscriptionPrice,
    activeSubscriptionPrice,
    isActiveInterval,
    isUpgrade,
    yearlyDiscountInPercent: calcYearlyDiscountPercent,
    isActiveSubscription,
    formattedSubscriptionPrice,
    downgradeDate,
    remainingDaysToDowngrade,
  };
};

export default useSubscription;
